import { Theme as AugmentedTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { Box, Divider, Grid, KeyValue, Pill, SectionHeader, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import {
  ApplicationDetails,
  EntryProofDownloadModel,
  ExamRegistrationType,
} from '../../../../api/candidate/exam/model';
import { parseLocationSearch } from '../../../../utils/StringUtils';
import DownloadSummaryPDFButton from '../../../../components/buttons/DownloadSummaryPDFButton';
import FormHeader from '../../../../components/form-items/FormHeader';
import EntryProofIcon from '../EntryProofIcon';
import { RegistrationType } from '../../../../api-refactored/entry-proof/useGetDownloadEntryProof';

const useStyles = makeStyles()((theme) =>
({
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  container: {
    paddingBottom: theme.spacing(2),
    maxWidth: 960,
  },
  statusContainer: {
    display: 'flex',
  },
  statusPill: {
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      paddingRight: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  },
  registrationType: {
    [theme.breakpoints.down('md')]: { alignSelf: 'flex-end' },
    fontWeight: 700,
    color: theme.color.state.stateInfo,
  },
  divider: {
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(-2),
    },
  },
}));

const entryProofRender = (props: ApplicationHeaderDetailsProps, classes: Record<string,string>) => {
  const entryProofLength = props?.entryProofDownloadModels?.length;
  switch (true) {
    case entryProofLength === 0: {
      return <Box className={classes.entryProofContainer}>-</Box>;
    }
    case entryProofLength === 1: {
      if (props?.entryProofDownloadModels && props.entryProofDownloadModels[0]) {
        const { fileName, uuid: fileUuid } = props.entryProofDownloadModels[0];
        if (fileName === null) {
          return <Box className={classes.entryProofContainer}>-</Box>;
        }
        return (
          <EntryProofIcon
            fileName={fileName}
            uuid={fileUuid}
            registrationType={RegistrationType.NATIONAL}
          />
        );
      }
      return <Box className={classes.entryProofContainer}>-</Box>;
    }
    case (entryProofLength as number) > 1: {
      if (
        props?.entryProofDownloadModels?.every(
          (entryProof: EntryProofDownloadModel) => entryProof.fileName === null
        )
      ) {
        return <Box className={classes.entryProofContainer}>-</Box>;
      }
      return props?.entryProofDownloadModels?.map((entryProof: EntryProofDownloadModel) => {
        if (entryProof.fileName === null) {
          return null;
        }
        return (
          <EntryProofIcon
            fileName={entryProof.fileName}
            uuid={entryProof.uuid}
            key={entryProof.uuid}
            registrationType={RegistrationType.NATIONAL}
          />
        );
      });
    }
    default: {
      return null;
    }
  }
};

interface ApplicationHeaderDetailsProps {
  applicationDetails: ApplicationDetails;
  onDownloadAction: () => void;
  isDownloading: boolean | undefined;
  displayRegistrationType?: boolean;
  candidateName?: string;
  isDisplayEntryProof: boolean;
  entryProofDownloadModels?: EntryProofDownloadModel[];
}

const ApplicationHeaderDetails = ({ ...props }: ApplicationHeaderDetailsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const applicationDateTime = props.applicationDetails.applicationDateTime
    ? stringToLongDateAndShortTime(props.applicationDetails.applicationDateTime)
    : 'N/A';

  const location = useLocation<any>();
  const { groupExamRegUuid } = parseLocationSearch(location.search);

  const getRegistrationType = () => {
    if (
      props.applicationDetails.examRegistrationType === ExamRegistrationType.SUPPLEMENTARY ||
      props.applicationDetails.examRegistrationType === ExamRegistrationType.LATE_SUPPLEMENTARY
    ) {
      return t('exam->>SupplementaryRegistration.SupplementaryRegistration');
    }
    return '';
  };

  return (
    <>
      <Grid container>
        <Grid xs={12}>
          <SectionHeader
            title={
              props.candidateName
                ? t('exam->>RegistrationSummary.GroupRegistrationDetails', {
                  candidateName: props.candidateName,
                })
                : t('exam->>RegistrationSummary.RegistrationDetails')
            }
          >
            {
                !groupExamRegUuid &&
                DownloadSummaryPDFButton({
                  isDownloading: props.isDownloading,
                  onDownloadAction: props.onDownloadAction,
                })
            }
          </SectionHeader>
        </Grid>
        <Grid xs={12}>
          <Box className={classes.container}>
            <Grid container direction="row">
              <Grid md={6} sm={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.ApplicationId')}
                  valueText={props.applicationDetails.applicationID}
                />
              </Grid>
              <Grid md={6} sm={12} xs={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.Status')}
                  valueText={
                    <div className={classes.statusContainer}>
                      <Grid className={classes.statusPill}>
                        <Pill
                          label={props.applicationDetails.applicationStatus}
                          variant="positive"
                        />
                      </Grid>
                      {props.displayRegistrationType && (
                        <Typography className={classes.registrationType}>
                          {getRegistrationType()}
                        </Typography>
                      )}
                    </div>
                  }
                />
              </Grid>
            </Grid>

            <Grid container direction="row">
              <Grid md={6} sm={12}>
                <KeyValue
                  keyText={t('exam->>RegistrationSummary.ApplicationDateTime')}
                  valueText={applicationDateTime}
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
        </Grid>
      </Grid>
      {props.isDisplayEntryProof && (
        <>
          <FormHeader title={t('exam->>RegistrationSummary.EntryProof')} />
          {entryProofRender(props, classes)}
          <Box className={classes.divider}>
            <Divider />
          </Box>
        </>
      )}
    </>
  );
};

export default ApplicationHeaderDetails;
